// TelcoTrack — detekce typu importního souboru podle názvu, listu a hlaviček.

import {
  ImportSourceType,
  normalizeCanonicalHeader,
} from '../constants/building-import-canonical.constants';

export type ClassifiedImportSource = ImportSourceType | 'UNKNOWN';

export interface ImportFileClassificationInput {
  fileName?: string;
  sheetName?: string;
  headers: string[];
}

export interface ImportFileClassificationResult {
  sourceType: ClassifiedImportSource;
  confidence: 'high' | 'medium' | 'low';
  reason: string;
}

function headerSet(headers: string[]): Set<string> {
  return new Set(headers.map((h) => normalizeCanonicalHeader(h)).filter(Boolean));
}

function hasAll(set: Set<string>, ...needles: string[]): boolean {
  return needles.every((n) => set.has(n));
}

function hasAny(set: Set<string>, ...needles: string[]): boolean {
  return needles.some((n) => set.has(n));
}

/**
 * Rozpozná typ importního souboru.
 * Pořadí: explicitní sheet/filename → signatury hlaviček.
 */
export function classifyImportSource(
  input: ImportFileClassificationInput,
): ImportFileClassificationResult {
  const fileName = (input.fileName ?? '').toLowerCase();
  const sheetName = normalizeCanonicalHeader(input.sheetName ?? '');
  const headers = headerSet(input.headers);

  if (sheetName === 'export_acq' || fileName.includes('report_acq')) {
    return {
      sourceType: ImportSourceType.EXPORT_ACQ,
      confidence: 'high',
      reason: 'sheet Export_ACQ nebo název Report_ACQ',
    };
  }

  if (sheetName === 'preakv' || fileName.includes('abc10') || fileName.includes('podzemni')) {
    if (hasAny(headers, 'stobj id', 'status rop id', 'ruian id')) {
      return {
        sourceType: ImportSourceType.PRE_AKV,
        confidence: 'high',
        reason: 'list preAkv / ABC xlsx se sloupci preAkv',
      };
    }
  }

  if (fileName.includes('rozv_gen') || fileName.includes('nazvy rozvadecu')) {
    return {
      sourceType: ImportSourceType.ROZV_GEN,
      confidence: 'high',
      reason: 'název souboru ROZV_GEN',
    };
  }

  if (
    hasAll(headers, 'rop_id') &&
    hasAny(headers, 'opt_kabel_vzdalenost', 'hdpe_vzdalenost', 'id_stav_obj')
  ) {
    return {
      sourceType: ImportSourceType.PRIPOJITELNY_ROP,
      confidence: 'high',
      reason: 'hlavičky seznamu připojitelných objektů',
    };
  }

  if (
    hasAll(headers, 'kia', 'rop id', 'stav rop') ||
    (fileName.includes('ropid') || fileName.includes('bitep3') || fileName.includes('zakladni_seznam_ropids'))
  ) {
    return {
      sourceType: ImportSourceType.ROPID_CSV,
      confidence: hasAll(headers, 'kia', 'rop id') ? 'high' : 'medium',
      reason: 'tabulka ROPID (BITEP3 / Tabulka ROPID)',
    };
  }

  if (hasAny(headers, 'nazev rozvadecu', 'nazev rozvadece')) {
    return {
      sourceType: ImportSourceType.ROZV_GEN,
      confidence: 'high',
      reason: 'sloupec název rozvaděče',
    };
  }

  if (hasAny(headers, 'stobj id', 'status rop id', 'ruian id', 'ap_cs id')) {
    return {
      sourceType: ImportSourceType.PRE_AKV,
      confidence: 'medium',
      reason: 'rozšířené sloupce preAkv',
    };
  }

  if (hasAll(headers, 'aqcid', 'status acq') && hasAny(headers, 'rop id', 'cislo popisne')) {
    if (hasAny(headers, 'pocet podlazi vc. prizemi', 'stobj id')) {
      return {
        sourceType: ImportSourceType.PRE_AKV,
        confidence: 'medium',
        reason: 'aqcid + preAkv sloupce',
      };
    }
    return {
      sourceType: ImportSourceType.EXPORT_ACQ,
      confidence: 'medium',
      reason: 'aqcid + status acq (Export_ACQ)',
    };
  }

  return { sourceType: 'UNKNOWN', confidence: 'low', reason: 'neznámá kombinace hlaviček' };
}

/** Vrátí preferovaný název listu pro parsování XLSX podle klasifikace. */
export function preferredSheetNamesForSource(sourceType: ClassifiedImportSource): string[] {
  switch (sourceType) {
    case ImportSourceType.PRE_AKV:
      return ['preAkv', 'List1'];
    case ImportSourceType.EXPORT_ACQ:
      return ['Export_ACQ'];
    case ImportSourceType.ROZV_GEN:
      return ['List1'];
    default:
      return [];
  }
}
